<template>
    <div class="rounded-lgr border border-valence-grey-250 bg-white p-7.5 flex flex-col gap-10">
        <div class="flex flex-col gap-4">
            <div class="flex items-center gap-2.5">
                <ArchetypeDot :letters="perspectiveReport.mbti_letters" class="size-8" />
                <h2 class="display-serif text-valence-grey-850">{{ archetypeTitle }}</h2>
            </div>
            <hr class="border-valence-grey-250" />
            <p class="body-small text-ink-muted">{{ archetypeIntro }}</p>
        </div>

        <div v-if="enrichedDimensions.length > 0" class="flex flex-col gap-5">
            <div v-for="dim in enrichedDimensions" :key="dim.dimension" class="flex flex-col gap-3">
                <div class="flex items-center gap-1.5">
                    <img :src="dim.icon" :alt="dim.displayName" class="size-5" />
                    <span class="body-small-semibold text-black">{{ dim.displayName }}</span>
                </div>

                <BaseTooltip
                    side="top"
                    :side-offset="10"
                    :as-child="false"
                    align="start"
                    :align-offset="tooltipAlignOffset(dim.barLeftPercent)"
                    :show-arrow="false"
                    :trigger-aria-label="`${dim.displayName} dimension tooltip`">
                    <template #trigger>
                        <DimensionBar ref="dimensionsRef" :code="dim.dimension" :binned-score="dim.binnedScore" :left-label="dim.leftLabel" :right-label="dim.rightLabel" />
                    </template>
                    <template #content>
                        <div class="flex items-center gap-1.5">
                            <PerspectiveDot :dimension="dim.dimension" size="md" />
                            <span class="whitespace-nowrap body-small">{{ dim.shortSummary }}</span>
                        </div>
                    </template>
                </BaseTooltip>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useElementSize } from "@vueuse/core";
import decisionMakingIcon from "~img/perspective/decision-making.svg";
import energyIcon from "~img/perspective/energy.svg";
import infoProcessingIcon from "~img/perspective/information-processing.svg";
import organizationIcon from "~img/perspective/organization.svg";
import type { PerspectiveReport } from "~vue/appTypes";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import ArchetypeDot from "~vue/Perspective/ArchetypeDot.vue";
import PerspectiveDot from "~vue/Perspective/PerspectiveDot.vue";
import { calculateDimensionSummary, type DimensionSummary } from "~vue/utils/perspectiveUtils";
import { computed, useTemplateRef } from "vue";

import DimensionBar from "./DimensionBar.vue";

type DimensionConfig = {
    icon: string;
    displayName: string;
};

const DIMENSION_CONFIG: Record<string, DimensionConfig> = {
    EI: { icon: energyIcon, displayName: "Energy" },
    SN: { icon: infoProcessingIcon, displayName: "Information Processing" },
    TF: { icon: decisionMakingIcon, displayName: "Decision Making" },
    JP: { icon: organizationIcon, displayName: "Organization" },
};

const DEFAULT_DIMENSION_CONFIG: DimensionConfig = {
    icon: energyIcon,
    displayName: "Unknown",
};

type EnrichedDimension = DimensionSummary & DimensionConfig & { binnedScore: number; barLeftPercent: number };

type PerspectiveCardProps = {
    perspectiveReport: PerspectiveReport;
};

const { perspectiveReport } = defineProps<PerspectiveCardProps>();

const archetypeTitle = computed(() => perspectiveReport.mbti_title ?? "");
const archetypeIntro = computed(() => perspectiveReport.mbti_intro ?? "");

const dimensionsRef = useTemplateRef<HTMLElement>("dimensionsRef");
const { width: trackWidth } = useElementSize(dimensionsRef);

const enrichedDimensions = computed<EnrichedDimension[]>(() => {
    const dimensions = perspectiveReport.dimensions ?? [];
    return dimensions.map((dimension) => {
        const summary = calculateDimensionSummary(dimension);
        const config = DIMENSION_CONFIG[dimension.dimension] ?? DEFAULT_DIMENSION_CONFIG;
        const barLeftPercent = Math.max(0, Math.min(summary.percent - 14, 72));
        return {
            ...summary,
            ...config,
            binnedScore: dimension.binned_score,
            barLeftPercent,
        };
    });
});

function tooltipAlignOffset(barLeftPercent: number): number {
    return Math.round((barLeftPercent / 100) * trackWidth.value);
}
</script>
